import type { BoardKind } from "@cleo/contracts";

import type { RoomRailGroups, RoomRailSessionNode } from "./projectRoomRailGroups";
import { CONTEXT_DOCK_SEATED_ROOMS, type WorkroomMenuEntry } from "./workroomLaunch";

/**
 * The room-card rail's one fold (the principal, 16 Oct 2026: "follow the same
 * pattern where what are now general sessions are just another room", after
 * the neucleos-3 cockpit rail). One card per Room the member can open: the
 * General card first, then every entitled Workroom in registry order, each
 * holding its open root sessions, newest first. A session Cleo launched for a
 * hand (its parent is a session, not a room) stays nested under that session.
 *
 * Laws:
 * - Every entitled Workroom is a card, with or without a World room or
 *   sessions yet; absence of entitlement means absence of the card.
 * - General folds every general room's root sessions into one card (the
 *   principal's choice of 16 Oct 2026); workroom rooms fold by workroomKey.
 * - A room whose seat lives on the context dock (Music) has no card.
 * - Personal order: pinned cards first, then the saved card order, then the
 *   default order. Nothing here persists; the caller owns preferences.
 * - A live search keeps a card whose label matches whole, otherwise only the
 *   matching sessions and their ancestors, and opens every card it keeps.
 */

export const GENERAL_CARD_ID = "general";

export interface RoomRailCard {
  /** `general`, or `workroom:<workroomKey>`. */
  readonly id: string;
  readonly kind: "general" | "workroom";
  readonly label: string;
  readonly workroomKey: string | null;
  /** The board a Workroom opens on; null for General. */
  readonly board: BoardKind | null;
  /** The World rooms folded into this card, born order. */
  readonly roomIds: ReadonlyArray<string>;
  /** Open root sessions, newest first; each carries its hand sessions. */
  readonly sessions: ReadonlyArray<RoomRailSessionNode>;
  readonly pinned: boolean;
  readonly collapsed: boolean;
  /** The addressed session is in this card, at any depth. */
  readonly addressed: boolean;
  /** Open sessions in this card, hands included. */
  readonly sessionCount: number;
}

export interface RoomRailCardChoices {
  readonly generalLabel?: string;
  readonly cardOrder?: ReadonlyArray<string>;
  readonly pinnedCardIds?: ReadonlyArray<string>;
  readonly collapsedCardIds?: ReadonlyArray<string>;
  readonly query?: string;
  /** `newest` (default) sorts root sessions newest first; `given` keeps the rooms' arranged order. */
  readonly sessionOrder?: "newest" | "given";
  /** Leave out cards with no sessions (a scope filter is on). */
  readonly hideEmpty?: boolean;
}

export const workroomCardId = (workroomKey: string): string => `workroom:${workroomKey}`;

/** The card a World room folds into. */
export const roomCardIdOf = (room: {
  readonly kind: string;
  readonly workroomKey: string | null;
}): string =>
  room.kind === "workroom" && room.workroomKey !== null
    ? workroomCardId(room.workroomKey)
    : GENERAL_CARD_ID;

export const isRoomCardId = (id: string): boolean =>
  id === GENERAL_CARD_ID || id.startsWith("workroom:");

function count(nodes: ReadonlyArray<RoomRailSessionNode>): number {
  return nodes.reduce((sum, node) => sum + 1 + count(node.children), 0);
}

function anyAddressed(nodes: ReadonlyArray<RoomRailSessionNode>): boolean {
  return nodes.some((node) => node.addressed || anyAddressed(node.children));
}

/** Keep matching sessions and the ancestors of a match; a match keeps its hands. */
function searchNodes(
  nodes: ReadonlyArray<RoomRailSessionNode>,
  needle: string,
): RoomRailSessionNode[] {
  return nodes.flatMap((node) => {
    if (node.session.label.toLowerCase().includes(needle)) return [node];
    const children = searchNodes(node.children, needle);
    return children.length > 0 ? [{ ...node, children }] : [];
  });
}

export function projectRoomRailCards(
  groups: RoomRailGroups,
  workrooms: ReadonlyArray<WorkroomMenuEntry>,
  choices: RoomRailCardChoices = {},
): ReadonlyArray<RoomRailCard> {
  const pinned = new Set(choices.pinnedCardIds ?? []);
  const collapsed = new Set(choices.collapsedCardIds ?? []);
  const needle = (choices.query ?? "").trim().toLowerCase();

  const newestFirst = (nodes: RoomRailSessionNode[]) =>
    choices.sessionOrder === "given"
      ? nodes
      : nodes.sort((a, b) => b.session.bornSeq - a.session.bornSeq);

  const generalRooms = groups.rooms.filter((entry) => entry.room.kind === "general");
  const drafts: Array<Omit<RoomRailCard, "pinned" | "collapsed" | "addressed" | "sessionCount">> = [
    {
      id: GENERAL_CARD_ID,
      kind: "general",
      label: choices.generalLabel ?? "General",
      workroomKey: null,
      board: null,
      roomIds: generalRooms.map((entry) => entry.room.roomId),
      sessions: newestFirst(generalRooms.flatMap((entry) => [...entry.sessions])),
    },
  ];
  const seen = new Set<string>();
  for (const workroom of workrooms) {
    if (seen.has(workroom.workroomKey) || CONTEXT_DOCK_SEATED_ROOMS.has(workroom.workroomKey))
      continue;
    seen.add(workroom.workroomKey);
    const rooms = groups.rooms.filter(
      (entry) => entry.room.kind === "workroom" && entry.room.workroomKey === workroom.workroomKey,
    );
    drafts.push({
      id: workroomCardId(workroom.workroomKey),
      kind: "workroom",
      label: workroom.title,
      workroomKey: workroom.workroomKey,
      board: workroom.board,
      roomIds: rooms.map((entry) => entry.room.roomId),
      sessions: newestFirst(rooms.flatMap((entry) => [...entry.sessions])),
    });
  }

  const saved = new Map((choices.cardOrder ?? []).map((id, i) => [id, i]));
  const defaultRank = new Map(drafts.map((draft, i) => [draft.id, i]));
  const rank = (id: string) => saved.get(id) ?? saved.size + defaultRank.get(id)!;
  const ordered = [...drafts].sort(
    (a, b) => Number(pinned.has(b.id)) - Number(pinned.has(a.id)) || rank(a.id) - rank(b.id),
  );

  return ordered.flatMap((draft) => {
    let sessions = draft.sessions;
    if (needle.length > 0 && !draft.label.toLowerCase().includes(needle)) {
      sessions = searchNodes(sessions, needle);
      if (sessions.length === 0) return [];
    }
    if (choices.hideEmpty === true && sessions.length === 0) return [];
    return [
      {
        ...draft,
        sessions,
        pinned: pinned.has(draft.id),
        collapsed: needle.length > 0 ? false : collapsed.has(draft.id),
        addressed: anyAddressed(draft.sessions),
        sessionCount: count(draft.sessions),
      },
    ];
  });
}

/** A hand session: one whose parent is a session (Cleo launched it), not a room. */
export const isHandSession = (node: RoomRailSessionNode): boolean =>
  !node.session.parentId.startsWith("R_");
